import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useCookies } from 'react-cookie';
import { 
    Flex, Box, Heading, FormControl, FormLabel, Input, Button, ButtonGroup, Text,
    InputGroup, InputRightElement, InputLeftAddon, InputRightAddon, Icon, Select, useDisclosure,
} from '@chakra-ui/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import { MdAddCircleOutline, MdFileOpen, } from 'react-icons/md';
import { FaFileExport } from 'react-icons/fa';
import { withNamespaces } from 'react-i18next';
import { SUCCESS_STATUS, FAILED_STATUS } from '../../../constants';
import "../../../styles/index.scss";

import { 
    apiLoadListPatient, apiLoadAPatient, apiCreateNewPatient, apiUpdateAPatient, apiDeleteAPatient, 
    apiExportPatientListToCSV, apiExportPatientListToExcel
} from '../../../redux/apis/PatientApis';
import { apiLoadListEnterprise } from '../../../redux/apis/EnterpriseApis'; 
import { clearAPatientDetail, clearPatientActionStatus, clearPatientListRemoteCSV, clearPatientListRemoteExcel } from '../../../redux/actions';
import { 
    getPatientList, getAPatient, getPatientActionStatus, getPatientActionMsg, getPatientError, getPatientTotalCount, getPatientOffset,
    getPatientListRemoteCSV, getPatientListRemoteExcel,
    getEnterpriseList
} from '../../../redux/selectors';

import NoDataPage from '../../../components/NoData';
import PatientList from './Patients/PatientList';
import ImportExcelPatientModal from '../Modals/ImportExcelPatientModal';
import AddEditPatientModal from '../Modals/AddEditPatientModal';
import { displayConfirmChoiceAlert } from '../../utils';

function Patient({ 
    patientList, patientDetail, actionStatus, actionMsg, error, totalCount, offset, remoteCSV, remoteExcel, enterpriseList,
    clearAPatientDetail, clearPatientActionStatus, 
    apiLoadListPatient, apiLoadAPatient, apiCreateNewPatient, apiUpdateAPatient, apiDeleteAPatient, 
    apiExportPatientListToCSV, clearPatientListRemoteCSV, apiExportPatientListToExcel, clearPatientListRemoteExcel,
    apiLoadListEnterprise, t
}) {
    const [ cookies ] = useCookies(["loginCookie"]);
    const [ exportFileType, setExportFileType ] = useState("EXCEL");
    const [ searchText, setSearchText ] = useState("");
    const [ isSearching, setIsSearching ] = useState(false);
    const [ isAction, setIsAction ] = useState(false);

    const addEditPatientModal = useDisclosure();
    const patientImportExcelModal = useDisclosure();

    const initialRef = useRef(null);
    const mounted = useRef();
    const limit = 10;
    const pageCount =  Math.ceil(totalCount / limit);

    useEffect(() => {
        if(!mounted.current) {
            apiLoadListPatient({ company_id: cookies?.loginCookie?.data?.company_id, limit, offset: 0 });
            apiLoadListEnterprise({ company_id: cookies?.loginCookie?.data?.company_id, order_by: "name", order_dir: "asc" });
            setIsAction(cookies.loginCookie?.data?.roles === "admin" || cookies.loginCookie?.data?.roles === "super_admin");
            mounted.current = true;
        } else {
            if(actionStatus === SUCCESS_STATUS) {
                if(addEditPatientModal.isOpen) {
                    addEditPatientModal.onClose();
                }
                toast.success(actionMsg, {
                    position: "bottom-right"
                });
                clearPatientActionStatus();
            } 
            else if(actionStatus === FAILED_STATUS) {       
                toast.error(error, {
                    position: "bottom-right"
                });
                clearPatientActionStatus();
            }
        }
    });

    useEffect(() => {
        setIsSearching(false);
    }, [patientList]);

    useEffect(() => {
        if (remoteCSV){
            const blobCSV = new Blob([remoteCSV], {type: 'text/csv;charset=UTF-16'});
            //Build a URL from the file
            const fileURL = window.URL.createObjectURL(blobCSV);
            const link = document.createElement('a');
            link.href = fileURL;
            link.setAttribute("download", `${cookies?.loginCookie?.data?.companyDetail?.name + "_DanhSachBenhNhan"}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            clearPatientListRemoteCSV();
        }
    }, [remoteCSV]);

    useEffect(() => {
        if (remoteExcel){
            const blobEXCEL = new Blob([remoteExcel], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'});
            //Build a URL from the file
            const fileURL = window.URL.createObjectURL(blobEXCEL);
            const link = document.createElement('a');
            link.href = fileURL;
            link.setAttribute("download", `${cookies?.loginCookie?.data?.companyDetail?.name + "_DanhSachBenhNhan"}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            clearPatientListRemoteExcel();
        }
    }, [remoteExcel]);

    const handleAddNewPatient = () => {
        clearAPatientDetail();
        addEditPatientModal.onOpen();
    }

    const handleExportFile = () => {
        displayConfirmChoiceAlert(`Bạn có muốn xuất dữ liệu ra file ${exportFileType}`, () => {
            if(exportFileType === "CSV") {
                apiExportPatientListToCSV({ 
                    company_id: cookies?.loginCookie?.data?.company_id,
                    user_id: cookies?.loginCookie?.data?.id,   
                });
            } else if(exportFileType === "EXCEL") {
                apiExportPatientListToExcel({ 
                    company_id: cookies?.loginCookie?.data?.company_id,
                    user_id: cookies?.loginCookie?.data?.id,   
                });
            } 
        })
    }

    const handleImportDataFinished = () => {
        apiLoadListPatient({ company_id: cookies?.loginCookie?.data?.company_id, limit, offset: 0 });
    }

    return (
        <React.Fragment>
            <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} borderBottomWidth="2px" px={8} py={4} gap={2} justify="space-between">
                <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} gap={2}>
                    <Button colorScheme='orange' isDisabled={!isAction} onClick={handleAddNewPatient}>
                        <Icon boxSize={6} as={MdAddCircleOutline} marginRight="8px"/>   
                        <Text fontSize="14px" fontWeight="400" textTransform="uppercase">{t("Add")}</Text>
                    </Button>

                    <Button colorScheme='teal' isDisabled={!isAction}
                        onClick={() => patientImportExcelModal.onOpen()}
                    >
                        <Icon boxSize={6} as={MdFileOpen} marginRight="8px"/>   
                        <Text fontSize="14px" fontWeight="400" textTransform="uppercase">{t("Import_Data_From_File")}</Text>
                    </Button>
                    
                    <InputGroup maxW="250px">
                        <Button paddingRight={1} borderTopRightRadius={0} borderBottomRightRadius={0} isDisabled={!isAction}
                             onClick={() => handleExportFile()}
                        >
                            <Icon boxSize={6} as={FaFileExport} marginRight="8px"/>  
                            <Text fontSize="14px" fontWeight="400" textTransform="uppercase">{t("Export_File")}</Text>
                        </Button>
                        <InputRightAddon p={0}>
                            <Select value={exportFileType} borderTopLeftRadius={0} borderBottomLeftRadius={0} isDisabled={!isAction}
                                onChange={(evt) => setExportFileType(evt.currentTarget.value)}
                            >
                                <option value="EXCEL">EXCEL</option>
                                <option value="CSV">CSV</option>
                            </Select>
                        </InputRightAddon>
                    </InputGroup>
                </Flex>

                <InputGroup width={{base: "100%", md: "40%", lg: "40%"}}>
                    <Input placeholder={t("Type_Search_Text")} onChange={(evt) => setSearchText(evt.currentTarget.value)} onKeyUp={(evt) => {
                        if(evt.key === "Enter") {
                            setIsSearching(true);
                            setTimeout(() => {
                                apiLoadListPatient({ searchText, company_id: cookies?.loginCookie?.data?.company_id });
                            }, 500); 
                        }
                    }}/>
                    <InputRightAddon p={0}>
                        <Button colorScheme='blue' 
                            isLoading={isSearching}
                            loadingText={t("Searching")}
                            onClick={() => {
                                setIsSearching(true);
                                setTimeout(() => {
                                    apiLoadListPatient({ searchText, company_id: cookies?.loginCookie?.data?.company_id });
                                }, 500);   
                            }
                        }>
                          {t("Search")}
                        </Button>
                    </InputRightAddon>
                </InputGroup>
            </Flex>
            
            <PatientList 
                items={patientList} itemsPerPage={limit} itemOffset={offset} pageCount={pageCount} 
                isAction={isAction} t={t}
                patientDetail={patientDetail} addEditPatientModal={addEditPatientModal}
                apiLoadListPatient={apiLoadListPatient} apiLoadAPatient={apiLoadAPatient}
                apiDeleteAPatient={apiDeleteAPatient}                       
            />
            {
                !(patientList && patientList.length > 0) && <NoDataPage text="Hiện tại không có dữ liệu"/>
            }
            {
                addEditPatientModal.isOpen &&
                <AddEditPatientModal 
                    initialRef={initialRef} isOpen={addEditPatientModal.isOpen} onClose={addEditPatientModal.onClose} 
                    company_id={cookies?.loginCookie?.data?.company_id} patientDetail={patientDetail} enterpriseList={enterpriseList} actionStatus={actionStatus}
                    apiCreateNewPatient={apiCreateNewPatient} apiUpdateAPatient={apiUpdateAPatient}
                />
            }
            {
                patientImportExcelModal.isOpen &&
                <ImportExcelPatientModal 
                    initialRef={initialRef} isOpen={patientImportExcelModal.isOpen} onClose={patientImportExcelModal.onClose} 
                    company_id={cookies?.loginCookie?.data?.company_id} onImportDataFinished={handleImportDataFinished}
                />
            }
            <ToastContainer />
        </React.Fragment>
    )
}

const mapStateToProps = (state) => {
    const patientList           =   getPatientList(state);
    const patientDetail         =   getAPatient(state);
    const actionStatus          =   getPatientActionStatus(state);
    const actionMsg             =   getPatientActionMsg(state);
    const error                 =   getPatientError(state);
    const totalCount            =   getPatientTotalCount(state);
    const offset                =   getPatientOffset(state);
    const remoteCSV             =   getPatientListRemoteCSV(state);
    const remoteExcel           =   getPatientListRemoteExcel(state);

    const enterpriseList        =   getEnterpriseList(state);

    return { patientList, patientDetail, actionStatus, actionMsg, error, totalCount, offset, remoteCSV, remoteExcel, enterpriseList };
}

const mapDispatchToProps = (dispatch) => ({
    apiLoadListPatient:         payload => dispatch(apiLoadListPatient(payload)),
    apiLoadAPatient:            payload => dispatch(apiLoadAPatient(payload)),
    apiCreateNewPatient:        payload => dispatch(apiCreateNewPatient(payload)),
    apiUpdateAPatient:          payload => dispatch(apiUpdateAPatient(payload)),
    apiDeleteAPatient:          payload => dispatch(apiDeleteAPatient(payload)),
    apiExportPatientListToCSV:  payload => dispatch(apiExportPatientListToCSV(payload)),
    apiExportPatientListToExcel: payload => dispatch(apiExportPatientListToExcel(payload)),


    apiLoadListEnterprise:      payload => dispatch(apiLoadListEnterprise(payload)),

    clearAPatientDetail:        payload => dispatch(clearAPatientDetail(payload)),
    clearPatientActionStatus:   payload => dispatch(clearPatientActionStatus(payload)),
    clearPatientListRemoteCSV:  payload => dispatch(clearPatientListRemoteCSV(payload)),
    clearPatientListRemoteExcel: payload => dispatch(clearPatientListRemoteExcel(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(Patient));